import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysInvoiceAiModel } from '../queryKeys';

import { fetchInvoiceAiModelSettings } from './fetchInvoiceAiModelSettings';
import { type InvoiceAiModelSettings } from './types';

/**
 * Generates the query options for the invoice AI model settings details query
 * so they can be shared between the useQuery hook and the prefetching.
 *
 * @param aiModelSettingsId - The ID of the invoice AI model settings to fetch.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getInvoiceAiModelSettingsQueryOptions = <
  TData = InvoiceAiModelSettings,
>({
  aiModelSettingsId,
  options,
}: {
  aiModelSettingsId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<InvoiceAiModelSettings, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: aiModelSettingsId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchInvoiceAiModelSettings(aiModelSettingsId, { signal })
      : skipToken,
    queryKey: queryKeysInvoiceAiModel.getSettings(aiModelSettingsId ?? ''),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the details of a single invoice AI model settings.
 *
 * @param aiModelSettingsId - The ID of the invoice AI model settings to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/get_invoice_ai_model_settings_details_vadmin_invoice_ai_model_settings__model_id__get
 */
export const useQueryInvoiceAiModelSettings = (
  aiModelSettingsId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<InvoiceAiModelSettings, Error, InvoiceAiModelSettings>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<InvoiceAiModelSettings, Error, InvoiceAiModelSettings>(
    getInvoiceAiModelSettingsQueryOptions<InvoiceAiModelSettings>({
      aiModelSettingsId,
      options,
    }),
  );
